import React from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from 'lucide-react';
import { Img, ScrollRow, Section, Shimmer } from '@/components/ui';
import { useVenuesWithEvents } from '@/hooks/useCatalog';

/**
 * "Happening at venues near you" — one card per approved provider that
 * currently has a live event, sorted by distance from the visitor.
 */
const VenueCard = ({ venue }) => (
  <Link to={`/venue/${venue.id}`} className="group block h-full">
    <article className="relative h-72 w-64 overflow-hidden rounded-2xl shadow-card transition-shadow hover:shadow-cardHover sm:w-72">
      <Img
        src={venue.cover}
        alt={venue.name}
        monogram={false}
        className="absolute inset-0 h-full w-full"
        imgClassName="transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

      {/* The text sits on its own frosted panel rather than relying on the
          gradient: venues upload anything as a cover — often a logo on white —
          and a gradient only helps when the photo underneath is already dark.
          The blur also flattens busy lettering behind the title. */}
      <div className="absolute inset-x-0 bottom-0 bg-black/70 p-4 text-white backdrop-blur-md">
        <h3 className="line-clamp-2 text-lg font-bold leading-tight">{venue.name}</h3>
        <p className="mt-1 line-clamp-1 text-xs text-white/75">
          {venue.distanceKm != null && (
            <span className="inline-flex items-center gap-1">
              <Navigation className="h-3 w-3" />
              {venue.distanceKm} km
            </span>
          )}
          {venue.distanceKm != null && venue.address ? ' • ' : ''}
          {venue.address}
        </p>

        {venue.nextEvent && (
          <div className="mt-3 border-t border-white/25 pt-3">
            <p className="text-[11px] font-bold uppercase tracking-widest text-white/75">Next up</p>
            <p className="mt-0.5 line-clamp-1 text-sm font-semibold">{venue.nextEvent.title}</p>
            <p className="mt-0.5 line-clamp-1 text-xs text-white/70">
              {venue.nextEvent.dateLabel}
              {venue.nextEvent.timeLabel ? `, ${venue.nextEvent.timeLabel}` : ''}
            </p>
          </div>
        )}
      </div>
    </article>
  </Link>
);

const VenuesNearYou = () => {
  const { venues, loading } = useVenuesWithEvents();

  if (!loading && venues.length === 0) return null;

  return (
    <Section
      title="Happening at venues near you"
      subtitle="Live listings from LocalKonnect partner venues"
    >
      {loading ? (
        <div className="flex gap-4 overflow-hidden">
          {Array.from({ length: 5 }).map((_, i) => (
            <Shimmer key={i} className="h-72 w-64 shrink-0 rounded-2xl sm:w-72" />
          ))}
        </div>
      ) : (
        <ScrollRow>
          {venues.map((venue) => (
            <VenueCard key={venue.id} venue={venue} />
          ))}
        </ScrollRow>
      )}
    </Section>
  );
};

export default VenuesNearYou;
export { VenueCard };
